import { AlertType, AlertProps } from "@ppb/the-wall-common/types";
import { calc, PotentialBet } from "@ppb/bet-engine";
import type {
  PriceValidation,
  SizeValidation,
} from "../store/state/exchange-betting-potential/ExchangeBettingPotential.types";
import { BettingMetadata } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { SportsbookOdds } from "@ppb/tbd-store/state/entities/SportsbookOdds.types";
import { BettingState, LEG_TYPES } from "@ppb/betslip-core";
import { formatOdds, formatTime } from "@ppb/formatters";
import { ExchangeBetTransactionError } from "../services/ExchangeBetTransactionError.types";
import { ObbPotentialBet } from "@ppb/tbd-store/state/betting/obb-betting/ObbBetting.types";
import type { BetslipExchangeMarketStatus } from "../store/state/exchange-markets/ExchangeMarkets.types";

import { isEachWayMarket } from "../helpers/exchange-betslip-helper";
import type { HandicapOutcome } from "../helpers/handicap-outcomes";
import type { BetSegmentRow, OutcomeBetSegmentsProps } from "./OutcomeBetSegments/OutcomeBetSegments.types";
import { currencyFormatWithDecimalPlaces } from "../formatters/currency-adapter";
import type { BetslipLocalization, BetslipOddsDisplay } from "../contract";
import { TranslationKey } from "../translations/keys";
import { i18n } from "../helpers/i18n";
import { toOdds } from "../formatters/odds-adapter";
import type { ExchangeSide } from "../types";

const ORDINAL_MAPPING: TranslationKey[] = [
  "I18N.BETSLIP.SBK.ORDINAL.FIRST",
  "I18N.BETSLIP.SBK.ORDINAL.SECOND",
  "I18N.BETSLIP.SBK.ORDINAL.THIRD",
];

function buildErrorInterpolationValues(
  e: ExchangeBetTransactionError,
  currencySymbol: string | undefined,
): { price: string; size: string; currencySymbol: string } | undefined {
  const { details } = e;
  if (details?.price && details?.size && currencySymbol) {
    return {
      price: `${details.price}`,
      size: `${details.size}`,
      currencySymbol,
    };
  }
  return undefined;
}

export function buildRacingTitle(metadata: BettingMetadata, localization: BetslipLocalization): string {
  if (metadata.type !== "RACING") {
    return metadata.eventName;
  }

  const { localeCodeBcp47, timezone } = localization;

  const { racing } = metadata;
  const formattedRaceTime = formatTime(racing.time, localeCodeBcp47, timezone);

  return `${formattedRaceTime} ${racing.venue}`;
}

export const buildExchangeTransactionalError = (
  e: ExchangeBetTransactionError,
  currencySymbol: string | undefined,
): AlertProps => {
  const key = `I18N.BETSLIP.ERROR.${e.errorCode}` as const;
  const interpolationValues = buildErrorInterpolationValues(e, currencySymbol);
  const message = i18n({ key, interpolationValues }) || i18n({ key: "I18N.BETSLIP.ERROR.UNKNOWN" });

  return { type: AlertType.Error, message };
};

const getMarketStatusTranslation = (marketStatus: BetslipExchangeMarketStatus): string => {
  switch (marketStatus) {
    case "SUSPENDED": {
      return i18n({ key: "I18N.BETSLIP.EXC.MARKET_SUSPENDED" });
    }
    case "CLOSED": {
      return i18n({ key: "I18N.BETSLIP.EXC.MARKET_CLOSED" });
    }
    default:
      throw new Error("Unexpected market status, must be SUSPENDED or CLOSED");
  }
};

export const getCastOrdinal = (value: number): string => {
  // There is no possibility of more than 3 runners in a cast
  const ordinalMapping = ORDINAL_MAPPING[value - 1];

  return i18n({ key: ordinalMapping }) || i18n({ key: "I18N.BETSLIP.SBK.ORDINAL.OTHER" });
};

export const buildMarketNotification = (marketStatus: BetslipExchangeMarketStatus): AlertProps => ({
  type: AlertType.Warning,
  message: getMarketStatusTranslation(marketStatus),
});

export const buildPriceNotification = (validation: PriceValidation): AlertProps => {
  switch (validation.reason) {
    case "ABOVE_MAX_PRICE": {
      return {
        type: AlertType.Warning,
        message: i18n({
          key: "I18N.BETSLIP.VALIDATION_ABOVE_MAX_PRICES",
          interpolationValues: { maximumOdds: `${validation.maximum}` },
        }),
        detail: i18n({ key: "I18N.BETSLIP.VALIDATION_SNAPPED_VALUE" }),
      };
    }
    case "INVALID_STEP": {
      return {
        type: AlertType.Warning,
        message: i18n({
          key: "I18N.BETSLIP.VALIDATION_INVALID_STEP",
          interpolationValues: {
            intervalMin: `${validation.interval.minimum}`,
            intervalMax: `${validation.interval.maximum}`,
            increment: `${validation.increment}`,
          },
        }),
        detail: i18n({ key: "I18N.BETSLIP.VALIDATION_SNAPPED_VALUE" }),
      };
    }
    case "BELOW_MIN_PRICE": {
      return {
        type: AlertType.Warning,
        message: i18n({
          key: "I18N.BETSLIP.VALIDATION_BELOW_MIN_PRICE",
          interpolationValues: { minimumOdds: `${validation.minimum}` },
        }),
        detail: i18n({ key: "I18N.BETSLIP.VALIDATION_SNAPPED_VALUE" }),
      };
    }
    default:
      throw new Error("Unexpected price ladder validation reason");
  }
};

export const buildSizeNotification = (validation: SizeValidation, newSize: string): AlertProps => {
  switch (validation.reason) {
    case "BELOW_MIN_SIZE": {
      return {
        type: AlertType.Warning,
        message: i18n({
          key: "I18N.BETSLIP.EXC.MIN_STAKE",
          interpolationValues: {
            minStake: `${newSize}`,
          },
        }),
      };
    }
    case "ABOVE_MAX_SIZE": {
      return {
        type: AlertType.Warning,
        message: i18n({
          key: "I18N.BETSLIP.VALIDATION_ABOVE_MAX_STAKE",
          interpolationValues: {
            maxStake: `${newSize}`,
          },
        }),
      };
    }
    case "INVALID_STEP": {
      return {
        type: AlertType.Warning,
        message: i18n({ key: "I18N.BETSLIP.VALIDATION_SNAPPED_VALUE" }),
      };
    }
    default:
      return {
        type: AlertType.Warning,
        message: i18n({ key: "I18N.BETSLIP.VALIDATION_SNAPPED_VALUE" }),
      };
  }
};

type BaseBet = {
  profit?: number;
  liability?: number;
};

type Bet = BaseBet & {
  side: ExchangeSide;
};

export type ConfirmBet = BaseBet & {
  side: ExchangeSide;
  size: number;
  liability: number;
};

type BetData = {
  label: string;
  value: string;
  rawValue: number;
};

export type EachWayExposure = { win: number; placed: number; lose: number };

const formatCalculation = (value: number, localization: BetslipLocalization): string =>
  currencyFormatWithDecimalPlaces({
    ...localization,
    value,
    decimalPlaces: 2,
  });

export const computeEachWayExposure = (
  bet: { side: ExchangeSide; selectionId: number; price?: number; size?: number },
  market: { marketType: string; bettingType: string; eachWayDivisor?: number },
): EachWayExposure | undefined => {
  if (!isEachWayMarket(market.marketType)) {
    return undefined;
  }

  const { price, size } = bet;
  const { eachWayDivisor } = market;

  if (!price || !size || !eachWayDivisor) {
    return undefined;
  }

  const potentialBet: PotentialBet = {
    id: "",
    orderType: "LIMIT",
    side: bet.side,
    selectionId: bet.selectionId,
    handicap: 0,
    price,
    size,
  };

  const engineMarket: Parameters<typeof calc.betExposure>[2] = {
    id: "",
    type: market.marketType,
    bettingType: market.bettingType,
    eachWayDivisor,
  };

  // TODO: Remove `EachWayExposure` cast once `@ppb/bet-engine`'s `calc.betExposure`
  // exposes a proper return type for each-way markets.
  return calc.betExposure(potentialBet, "UNMATCHED", engineMarket, []) as EachWayExposure;
};

/**
 * The outcome rows for display: the label resolved and interpolated, the profit formatted as money.
 * One row per goal margin, for `OutcomeBetSegments`.
 */
export const getHandicapOutcomeRows = (
  outcomes: HandicapOutcome[],
  localization: BetslipLocalization,
): BetSegmentRow[] =>
  outcomes.map(({ labelKey, params, pnl }) => ({
    term: i18n({
      key: labelKey,
      // `param1` / `param2` are EMS's own names, carried through so the keys stay drop-in.
      interpolationValues: Object.fromEntries(
        Object.entries(params).filter((entry): entry is [string, string | number] => entry[1] !== undefined),
      ),
    }),
    value: formatCalculation(pnl, localization),
    rawValue: pnl,
  }));

export type EachWayReceiptOverrides = {
  profit: number;
  liability?: number;
};

export const getEachWayReceiptOverrides = (
  side: ExchangeSide,
  exposure: EachWayExposure | undefined,
): EachWayReceiptOverrides | undefined => {
  if (!exposure) {
    return undefined;
  }

  if (side === "BACK") {
    return { profit: exposure.win };
  }

  return { profit: exposure.lose, liability: Math.abs(exposure.win) };
};

export const getBetData = (
  bet: Bet & { size?: number },
  localization: BetslipLocalization,
  isDepositRequired: boolean,
  eachWayExposure?: EachWayExposure,
): BetData => {
  const defaultBetData = {
    label: "",
    value: "",
    rawValue: 0,
  };

  if (isDepositRequired) {
    return defaultBetData;
  }

  const eachWayOverride = eachWayExposure
    ? bet.side === "BACK"
      ? eachWayExposure.win
      : Math.abs(eachWayExposure.win)
    : undefined;

  if (bet.side === "BACK" && (eachWayOverride !== undefined || bet.profit !== undefined)) {
    const rawValue = eachWayOverride ?? (bet.profit as number);

    return {
      label: `${i18n({ key: "I18N.BETSLIP.PROFIT" })}:`,
      value: formatCalculation(rawValue, localization),
      rawValue,
    };
  }

  if (bet.side === "LAY" && (eachWayOverride !== undefined || bet.liability !== undefined)) {
    const rawValue = eachWayOverride ?? Math.max(0, bet.liability as number);

    return {
      label: `${i18n({ key: "I18N.BETSLIP.LIABILITY" })}:`,
      value: formatCalculation(rawValue, localization),
      rawValue,
    };
  }

  return defaultBetData;
};

export type OutcomeSegmentsData = Pick<OutcomeBetSegmentsProps, "rows">;

/** The each-way outcome rows, in order: if win, if placed, if unplaced. */
export const getEachWayBetData = (
  exposure: EachWayExposure | undefined,
  localization: BetslipLocalization,
): OutcomeSegmentsData | undefined => {
  if (!exposure) {
    return undefined;
  }

  const row = (key: TranslationKey, pnl: number): BetSegmentRow => ({
    term: i18n({ key }),
    value: formatCalculation(pnl, localization),
    rawValue: pnl,
  });

  return {
    rows: [
      row("I18N.EACH_WAY.IF_WIN", exposure.win),
      row("I18N.EACH_WAY.IF_PLACED", exposure.placed),
      row("I18N.EACH_WAY.IF_UNPLACED", exposure.lose),
    ],
  };
};

export const getPotentialExposure = <T extends ConfirmBet>(bet: T): number =>
  bet.side === "LAY" ? bet.liability : bet.size;

export const buildFreeBetsLabel = (
  translationKey: TranslationKey,
  localization: BetslipLocalization,
  bonusUsed: number,
): string => {
  const valueWithCurrency = currencyFormatWithDecimalPlaces({ ...localization, value: bonusUsed });

  return i18n({
    key: translationKey,
    interpolationValues: { bonus: valueWithCurrency },
  });
};

export const buildAccaInsuranceLabels = (
  isAccaInsuranceSelected: boolean,
): {
  accaInsuranceTitle: string;
  accaInsuranceSubtitle: string;
  accaInsuranceTermsLabel: string;
} => {
  const accaInsuranceTitle = isAccaInsuranceSelected
    ? i18n({ key: "I18N.BETSLIP.ACCA_INSURANCE_APPLIED" })
    : i18n({ key: "I18N.BETSLIP.APPLY_ACCA_INSURANCE" });

  const accaInsuranceSubtitle = i18n({ key: "I18N.BETSLIP.MESSAGING.DESCRIPTION.ACCA_INSURANCE_PLACE" });
  const accaInsuranceTermsLabel = i18n({ key: "I18N.BETSLIP.ACCA_INSURANCE_TERMS_LABEL" });

  return {
    accaInsuranceTitle,
    accaInsuranceSubtitle,
    accaInsuranceTermsLabel,
  };
};

export const buildOriginalPotentialReturns = (
  totalStake: number | null,
  totalOriginalPotentialReturns: number | null,
  localization: BetslipLocalization,
): string | undefined => {
  if (!totalStake) {
    return currencyFormatWithDecimalPlaces({
      ...localization,
      value: 0,
      decimalPlaces: 2,
    });
  }

  if (!totalOriginalPotentialReturns) {
    return undefined;
  }

  return currencyFormatWithDecimalPlaces({
    ...localization,
    value: totalOriginalPotentialReturns,
    decimalPlaces: 2,
  });
};
export const buildPotentialReturns = (
  totalStake: number | null,
  totalPotentialReturns: number | null,
  localization: BetslipLocalization,
): string => {
  if (!totalStake) {
    return currencyFormatWithDecimalPlaces({
      ...localization,
      value: 0,
      decimalPlaces: 2,
    });
  }

  if (!totalPotentialReturns) {
    return i18n({ key: "I18N.BETSLIP.TBD" });
  }

  return currencyFormatWithDecimalPlaces({
    ...localization,
    value: totalPotentialReturns,
    decimalPlaces: 2,
  });
};

export const buildTotalOriginalReturns = (
  totalStake: number | null,
  totalPotentialReturns: number | null,
  totalOriginalReturns: number | null,
  hasPriceBoostedCombination: boolean,
  localization: BetslipLocalization,
): string | undefined => {
  if (!totalStake || !totalPotentialReturns || !totalOriginalReturns || !hasPriceBoostedCombination) {
    return undefined;
  }

  return currencyFormatWithDecimalPlaces({
    ...localization,
    value: totalOriginalReturns,
    decimalPlaces: 2,
  });
};

const isStartingPrice = (combination: BettingState.Combination, legs: BettingState.LegsMap | undefined) =>
  (!!legs && combination.legs.some((legId) => !legs[legId].odds)) ||
  (combination.isSPAvailable && combination.isSPSelected);

const isOneLineBetOnly = (combination: BettingState.Combination, legs: BettingState.LegsMap | undefined) =>
  !!legs && !!combination && combination.legs.every((legId) => legs[legId].legType === LEG_TYPES.ONE_LINE_BET);

export const buildOdds = (
  displayOdds: null | BettingState.Odds,
  preference: BetslipOddsDisplay,
  isPriceBoostSelected = false,
): string =>
  displayOdds
    ? formatOdds(toOdds(displayOdds), preference, isPriceBoostSelected)
    : i18n({ key: "I18N.BETSLIP.STARTING_PRICE" });

export const buildCombinationOdds = (
  combination: BettingState.Combination,
  preference: BetslipOddsDisplay,
  legs?: BettingState.LegsMap,
): string => {
  if (isOneLineBetOnly(combination, legs)) {
    return buildOdds(combination.displayOdds, preference);
  }

  if (isStartingPrice(combination, legs)) {
    return buildOdds(null, preference);
  }

  if (combination.isAccaInsuranceSelected && combination.accaInsuranceDisplayOdds) {
    return buildOdds(combination.accaInsuranceDisplayOdds, preference);
  }

  if (combination.isPriceBoostSelected && combination.priceBoostDisplayOdds) {
    return buildOdds(combination.priceBoostDisplayOdds, preference);
  }

  return buildOdds(combination.displayOdds, preference);
};

export const buildObbPotentialBetOdds = (quote: ObbPotentialBet["quote"], preference: BetslipOddsDisplay): string => {
  if (!quote) {
    return i18n({ key: "I18N.BETSLIP.NOT_AVAILABLE" });
  }

  return formatOdds(quote.price, preference);
};

export const buildCombinationPreviousOdds = (
  currentOdds: BettingState.Odds | null,
  previousOdds: SportsbookOdds | undefined,
  isPriceBoostSelected: boolean,
  sportsbookOddsDisplay: BetslipOddsDisplay,
): string | undefined => {
  if (isPriceBoostSelected && currentOdds) {
    return formatOdds(toOdds(currentOdds), sportsbookOddsDisplay);
  }

  return previousOdds ? formatOdds(previousOdds, sportsbookOddsDisplay) : undefined;
};

export const buildSportsbookFreeBetsLabel = (
  totalBonusUsed: number | undefined,
  localization: BetslipLocalization,
  translationKey: TranslationKey,
): string => {
  if (typeof totalBonusUsed === "undefined") {
    return "";
  }

  if (totalBonusUsed === 0) {
    return i18n({ key: "I18N.BETSLIP.BONUS_NOT_AVAILABLE" });
  }

  return buildFreeBetsLabel(translationKey, localization, totalBonusUsed);
};
